import type {
  ColumnSort,
  GroupingState,
  PaginationState,
  RowData,
  SortingState,
} from "@tanstack/react-table";
import { t as tStatic, type TFunction } from "i18next";
import _, { get } from "lodash";
import type { SetterOrUpdater } from "recoil";

import {
  FIELD_TYPE_IDS,
  GROUPING_FIELD_LIVE_MAP,
  GROUPING_GRID_NO_GROUPS,
  GroupingGridObjectNoGroups,
  NO_GROUPING_QS_VALUE,
  TABLE_PAGE_SIZE_GROUPING,
  TABLE_PAGE_SIZE_UNGROUPED,
} from "../constants";
import type { IExtendableDto, IRideMetadata, ITableDto } from "../dto";
import { RideDelayStatusEnum, SortingEnum } from "../enums";
import type {
  DtoFieldType,
  GroupingDirection,
  IColumnDefMeta,
  ICourseMetadata,
  ICustomField,
  IFormAttributes,
  IGridAttributes,
  IRydeCell,
  IRydeHeader,
  ITypeormWhereCriteria,
  RydeUiUnit,
} from "../models";
import { excludeNotArrayValue } from "./array.helpers";
import {
  appendMetadataPrefix,
  getComputedGridFieldName,
} from "./dynamic-forms.helpers";
import { getEntitySingleLabelKey } from "./dynamic-forms.translate.helpers";
import { isSomeEnum } from "./enum.helpers";
import { formatPrice } from "./format-number";
import { distanceFormula, durationInMinutesCeil } from "./formula.helpers";
import { getRideDelayStatusSortRank } from "./live-map/live-map.helpers";
import { Log } from "./log.helpers";
import { hasKeys } from "./objects.helpers";
import { isPhoneSearchMatch } from "./phone.helpers";
import {
  getSpecificSearchPatterMatch,
  parseSpecificSearchStringToArray,
} from "./search.helpers";
import { compareBasic, compareText } from "./sorting.helpers";
import { isEmptyValue } from "./type.helpers";

const TOTAL_DURATION_FIELD: keyof IRideMetadata & keyof ICourseMetadata =
  "totalDuration";
const TOTAL_DISTANCE_FIELD: keyof IRideMetadata & keyof ICourseMetadata =
  "totalDistance";

/**
 * The "--" (select none) filter option is sent as an empty entry alongside the regularly selected ids,
 * so its presence is what tells a client-side filter to keep rows that have no value for the field.
 */
const hasEmptyFilterValue = (filterValues: unknown[]): boolean =>
  filterValues.some(filterValue => isEmptyValue(filterValue));

/**
 * The entries a row value may legitimately be compared against. The "--" marker is deliberately not one
 * of them: it states "this row has no value", so matching it against a value held *inside* a row (an
 * array with a null element, say) would report a row as empty while it has values.
 */
const selectedFilterValues = (
  filterValues: unknown[],
): (number | string | boolean)[] =>
  filterValues.filter(
    (filterValue): filterValue is number | string | boolean =>
      !isEmptyValue(filterValue),
  );

interface IClientSearchMatcherArguments {
  row: IExtendableDto;
  fieldName: string;
  searchValue: string;
}

export interface ITableClientSearch {
  value: string;
  allowedFieldNamesResolved: string[];
  /** The searchable fields holding a phone, matched by its local form as well. */
  phoneFieldNamesResolved?: string[];
}

type TClientSearchMatcher = ({
  row,
  fieldName,
  searchValue,
}: IClientSearchMatcherArguments) => boolean;

export const getSearchTokens = (input: string | null | undefined): string[] => {
  const normalizedInput = input?.toLowerCase()?.trim() || "";
  return normalizedInput.split(/\s+/).filter(token => token.length > 0);
};

/**
 * Every search token must appear in one of `texts`, e.g. a translated label or the code name behind it.
 * Joined on a newline, which a token never contains, so no token can match across two texts.
 */
export const isSearchTokensMatch = ({
  texts,
  searchValue,
}: {
  texts: (string | null | undefined)[];
  searchValue: string;
}): boolean => {
  const searchableText = texts.filter(Boolean).join("\n").toLowerCase();
  return getSearchTokens(searchValue).every(token =>
    searchableText.includes(token),
  );
};

export const searchMatchers: Record<string, TClientSearchMatcher> = {
  defaultMatcher: ({ row, fieldName, searchValue }) => {
    const rawValue = get(row, fieldName);

    if (!rawValue) {
      return false;
    }

    return isSearchTokensMatch({ texts: [String(rawValue)], searchValue });
  },
  // Admin lists show the translated entity name, so search accepts it as well as the code name.
  entityTitle: ({ row, fieldName, searchValue }) => {
    const entityName = get(row, fieldName);

    if (!entityName) {
      return false;
    }

    return isSearchTokensMatch({
      texts: [
        String(entityName),
        tStatic(getEntitySingleLabelKey(String(entityName)), {
          defaultValue: "",
        }),
      ],
      searchValue,
    });
  },
  "metadata.passengersInfo": ({ row, fieldName, searchValue }) => {
    const passengersInfo = get(
      row,
      fieldName,
    ) as IRideMetadata["passengersInfo"];

    if (!Array.isArray(passengersInfo) || passengersInfo.length === 0) {
      return false;
    }

    const queryTokens = getSearchTokens(searchValue);

    return passengersInfo.some(({ firstName, lastName, code }) => {
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const codeStr = code?.toLowerCase() ?? "";

      return queryTokens.every(
        token => fullName.includes(token) || codeStr.includes(token),
      );
    });
  },
};

/** A phone is read in its local form ("0522345678") but stored international, so a search hits either. */
export const phoneSearchMatcher: TClientSearchMatcher = ({
  row,
  fieldName,
  searchValue,
}) =>
  searchMatchers.defaultMatcher({ row, fieldName, searchValue }) ||
  isPhoneSearchMatch({
    rawPhone: String(get(row, fieldName) ?? ""),
    searchValue,
  });

const getMatchHandler = (fieldName: string): TClientSearchMatcher => {
  if (searchMatchers[fieldName] !== undefined) {
    return searchMatchers[fieldName];
  }

  return searchMatchers.defaultMatcher;
};

export class TableHelpers {
  static getUniqueGroupValues = <T>(
    rows: T[] | undefined,
    groupingField?: keyof T,
  ): T[keyof T][] | undefined[] => {
    if (groupingField === undefined || !rows) {
      return GroupingGridObjectNoGroups;
    }

    return [...new Set(rows.map(row => _.get(row, groupingField)))];
  };

  static getOrderedGroupValues = <T>(
    values: T[] | undefined,
    groupingDirection: GroupingDirection,
  ): T[] => {
    return _.orderBy(values, [], [groupingDirection]);
  };

  static getGroupedRows = <T extends ITableDto>({
    rows,
    groupingFieldValue,
    groupingFieldNameResolved,
  }: {
    rows: T[] | undefined;
    groupingFieldValue: DtoFieldType | typeof GROUPING_GRID_NO_GROUPS;
    groupingFieldNameResolved: string | number | undefined;
  }): T[] => {
    if (!rows?.length) {
      return [];
    }

    if (
      groupingFieldNameResolved === undefined ||
      groupingFieldNameResolved === NO_GROUPING_QS_VALUE ||
      groupingFieldValue === GROUPING_GRID_NO_GROUPS
    ) {
      return rows;
    }

    return rows.filter(
      row => _.get(row, groupingFieldNameResolved) === groupingFieldValue,
    );
  };

  static getFilteredRows = <T extends IExtendableDto>({
    rows,
    filters,
    clientSearch,
    limitIds,
  }: {
    rows: T[] | undefined;
    filters?: ITypeormWhereCriteria<T>;
    clientSearch?: ITableClientSearch;
    limitIds?: number[] | null;
  }): T[] => {
    if (!rows?.length) {
      return [];
    }

    const limitIdsSet = limitIds ? new Set(limitIds) : null;
    let resultRows = limitIdsSet
      ? rows.filter(r => limitIdsSet.has(r.id))
      : rows;

    const isSearchPatternSpecific = getSpecificSearchPatterMatch(
      clientSearch?.value ?? "",
    );

    if (
      clientSearch?.allowedFieldNamesResolved &&
      clientSearch.value &&
      !isSearchPatternSpecific
    ) {
      const phoneFieldNames = new Set(clientSearch.phoneFieldNamesResolved);

      resultRows = resultRows.filter(row => {
        return clientSearch.allowedFieldNamesResolved.some(
          fieldNameResolved => {
            const matchHandler = phoneFieldNames.has(fieldNameResolved)
              ? phoneSearchMatcher
              : getMatchHandler(fieldNameResolved);

            return matchHandler({
              row,
              fieldName: fieldNameResolved,
              searchValue: clientSearch.value,
            });
          },
        );
      });
    } else if (clientSearch?.value && isSearchPatternSpecific) {
      const searchValueIds = parseSpecificSearchStringToArray(
        clientSearch.value,
      );

      if (searchValueIds?.length) {
        const searchValueIdsSet = new Set(searchValueIds);
        resultRows = resultRows.filter(({ id }) => searchValueIdsSet.has(id));
      }
    }

    if (!hasKeys(filters)) {
      return resultRows;
    }

    for (const fieldName in filters) {
      // Use hasOwnProperty to filter unwanted properties from prototype (guard-for-in rule)
      if (!Object.prototype.hasOwnProperty.call(filters, fieldName)) {
        continue;
      }

      const inValue = filters[fieldName]?.$in;

      if (inValue) {
        const inValueArray = excludeNotArrayValue(inValue);
        const inValueSet = new Set<unknown>(selectedFilterValues(inValueArray));
        // A boolean "No" filter selects `false`, but rows whose toggle was never set have no stored
        // value. Treat a missing value as `false` so they match the "No" option (RYDE-7544). For
        // non-boolean filters `false` is never selected, so missing values keep not matching as before.
        const shouldMatchMissingAsFalse = inValueArray.includes(false);
        const isEmptyValueSelected = hasEmptyFilterValue(inValueArray);
        resultRows = resultRows.filter(row => {
          const fieldValue = get(row, fieldName);

          if (isEmptyValue(fieldValue)) {
            return isEmptyValueSelected || shouldMatchMissingAsFalse;
          }

          return inValueSet.has(fieldValue);
        });
      }

      const overlapsValue = filters[fieldName]?.$overlaps;

      if (overlapsValue && Array.isArray(overlapsValue)) {
        const overlapsValueSet = new Set<unknown>(
          selectedFilterValues(overlapsValue),
        );
        const isEmptyValueSelected = hasEmptyFilterValue(overlapsValue);
        resultRows = resultRows.filter(row => {
          const fieldValues = excludeNotArrayValue(get(row, fieldName));

          if (!fieldValues.length) {
            return isEmptyValueSelected;
          }

          return _.some(fieldValues, value => overlapsValueSet.has(value));
        });
      }

      const containsValue = filters[fieldName]?.$contains;

      if (containsValue && Array.isArray(containsValue)) {
        const containsValueSet = new Set(containsValue as (number | string)[]);
        resultRows = resultRows.filter(row => {
          return _.every(
            row[fieldName as keyof T] as string[] | number[],
            value => containsValueSet.has(value as string | number),
          );
        });
      }
    }

    return resultRows;
  };

  static getGroupByField = (
    columnGrouping: GroupingState,
  ): string | undefined => {
    const groupingField = columnGrouping[0];

    if (groupingField === NO_GROUPING_QS_VALUE) {
      return undefined;
    }

    return groupingField;
  };

  /**
   * An empty grouping is ungrouped, same as "none". The grid pager and the server fetch both take their size
   * from here, so a fetched page never holds fewer rows than the pager counts.
   */
  static getPageSize = (columnGrouping: GroupingState): number => {
    const isUngrouped =
      !columnGrouping?.length ||
      (columnGrouping.length === 1 &&
        columnGrouping[0] === NO_GROUPING_QS_VALUE);

    return isUngrouped ? TABLE_PAGE_SIZE_UNGROUPED : TABLE_PAGE_SIZE_GROUPING;
  };

  static getDefaultPagination = (
    columnGrouping: GroupingState,
  ): PaginationState => {
    const pageSize = TableHelpers.getPageSize(columnGrouping);

    return {
      pageIndex: 0,
      pageSize,
    };
  };

  static getOrderByField = (
    columnSorting: SortingState,
  ): string | undefined => {
    return columnSorting[0]?.id;
  };

  static getOrderByDirection = (
    columnSorting: SortingState,
  ): SortingEnum | undefined => {
    if (columnSorting.length > 0) {
      return columnSorting[0]?.desc ? SortingEnum.Desc : SortingEnum.Asc;
    }

    return undefined;
  };
}

/** Alias used by server-pagination fetch hooks. */
export const tableHelpers = TableHelpers;

export const getHeaderClassName = <T extends RowData>(
  header: IRydeHeader<T, unknown>,
): string =>
  (header.column.columnDef.meta as IColumnDefMeta)?.classNameHeader ||
  `col_${header.id}`;

export const getCellClassName = <T extends RowData>(
  cell: IRydeCell<T, unknown>,
  customClassName?: string,
): string => {
  let className =
    "grid_table_cell " +
    ((cell.column.columnDef.meta as IColumnDefMeta)?.classNameCell ||
      `cell_${cell.id}`);

  if (customClassName) {
    className += ` ${customClassName}`;
  }

  return className;
};

export const getCellDataAttributes = <T extends RowData>(
  cell: IRydeCell<T, unknown>,
): Record<string, unknown> | undefined => {
  const metadataAttributeField = (cell.column.columnDef.meta as IColumnDefMeta)
    ?.dataAttributeField;

  if (!metadataAttributeField) {
    return undefined;
  }

  return {
    [`data-${metadataAttributeField}`]: cell.row.getValue(
      metadataAttributeField,
    ),
  };
};

const getSortingFunction = <T>(value: unknown): ((a: T, b: T) => number) => {
  if (typeof value === "string") {
    return compareText;
  }

  return compareBasic;
};

export const getSortedGroupValuesFromTable = <T>(
  groupValues: T[],
  sorting: ColumnSort,
): T[] | undefined[] => {
  if (!groupValues.length) {
    Log.error(tStatic("errors.cantSortGroupedGridGroupValuesIsEmpty"), sorting);
    return groupValues;
  }

  const sortingFunction = getSortingFunction(groupValues.at(0));

  return groupValues.sort((...args) => {
    return sorting.desc ? sortingFunction(...args) : -sortingFunction(...args);
  });
};

const LIVE_MAP_DELAY_GROUPING_FIELDS = [
  GROUPING_FIELD_LIVE_MAP,
  `metadata.${GROUPING_FIELD_LIVE_MAP}`,
] as const;

export const isLiveMapDelayStatusGroupingColumn = (
  columnName: string | null | undefined,
  gridFieldTypeId?: number | null,
): boolean => {
  if (gridFieldTypeId === FIELD_TYPE_IDS.GRID.live_map_delay_status) {
    return true;
  }

  if (!columnName) {
    return false;
  }

  return LIVE_MAP_DELAY_GROUPING_FIELDS.includes(
    columnName as (typeof LIVE_MAP_DELAY_GROUPING_FIELDS)[number],
  );
};

export const findLiveMapDelayStatusGridControl = (
  gridControls: readonly ICustomField[],
): ICustomField | undefined =>
  gridControls.find(
    c =>
      c.name === GROUPING_FIELD_LIVE_MAP ||
      c.gridFieldTypeId === FIELD_TYPE_IDS.GRID.live_map_delay_status,
  );

/** Detects live-map grouping by delay status (column id, field name, or grid control id). */
export const isRideMapDelayStatusGroupingField = (
  groupingField: string | number | undefined,
  gridControls?: readonly ICustomField[],
): boolean => {
  if (groupingField === undefined || groupingField === NO_GROUPING_QS_VALUE) {
    return false;
  }

  const fieldStr = String(groupingField);
  if (isLiveMapDelayStatusGroupingColumn(fieldStr)) {
    return true;
  }

  if (!gridControls?.length) {
    return false;
  }

  const delayControl = findLiveMapDelayStatusGridControl(gridControls);
  if (!delayControl) {
    return false;
  }

  return (
    fieldStr === String(delayControl.id) ||
    fieldStr === delayControl.name ||
    fieldStr === getComputedGridFieldName(delayControl)
  );
};

/** Row path used for grouping/map lookup (`statusLate` root field, not metadata). */
export const resolveRideMapDelayStatusGroupingFieldName = (
  groupingField: string | number | undefined,
  gridControls?: readonly ICustomField[],
): string | undefined => {
  if (!isRideMapDelayStatusGroupingField(groupingField, gridControls)) {
    return undefined;
  }

  const delayControl = gridControls?.length
    ? findLiveMapDelayStatusGridControl(gridControls)
    : undefined;

  if (delayControl) {
    return getComputedGridFieldName(delayControl);
  }

  const fieldStr = String(groupingField);
  return isLiveMapDelayStatusGroupingColumn(fieldStr)
    ? fieldStr
    : GROUPING_FIELD_LIVE_MAP;
};

/** Legacy live-map group header order: No Show → Late → … → Not tracked. */
export const getOrderedLiveMapDelayStatusGroupValues = <T>(
  values: readonly T[],
  groupingDirection: GroupingDirection = "asc",
): T[] => {
  const normalized = [
    ...new Set(
      values.map(value => normalizeLiveMapDelayStatusGroupValue(value) as T),
    ),
  ];

  const sorted = [...normalized].sort(
    (a, b) => getRideDelayStatusSortRank(a) - getRideDelayStatusSortRank(b),
  );

  return groupingDirection === "desc" ? sorted.reverse() : sorted;
};

/** Normalizes live-map group keys so empty rows share the NOT_TRACKED bucket. */
export const normalizeLiveMapDelayStatusGroupValue = (
  groupValue: unknown,
): RideDelayStatusEnum | unknown => {
  if (groupValue === undefined || groupValue === null || groupValue === "") {
    return RideDelayStatusEnum.NOT_TRACKED;
  }

  return groupValue;
};

export const isGroupingValueDelayEnum = (
  columnName: string | null | undefined,
  groupingValue: unknown,
  gridFieldTypeId?: number | null,
): boolean => {
  if (!isLiveMapDelayStatusGroupingColumn(columnName, gridFieldTypeId)) {
    return false;
  }

  return isSomeEnum(RideDelayStatusEnum)(groupingValue);
};

export const updateListEntity = (
  setter:
    | SetterOrUpdater<IExtendableDto[] | undefined>
    | SetterOrUpdater<IExtendableDto[]>,
  dto: IExtendableDto,
): void => {
  setter((prevState: IExtendableDto[] | undefined) => {
    if (prevState === undefined) {
      return [dto];
    }

    const index = prevState.findIndex(
      prevStateDto => prevStateDto.id === dto.id,
    );

    return prevState.toSpliced(index, 1, dto);
  });
};

/** Live map grid uses `rideMap` page context, but field update permissions live on `ride`. */
export const resolveGridPermissionEntity = (
  pageEntity: RydeUiUnit,
): RydeUiUnit => (pageEntity === "rideMap" ? "ride" : pageEntity);

const isDurationDistanceEntity = (entity: RydeUiUnit): boolean => {
  return entity === "ride" || entity === "rideMap" || entity === "course";
};

interface INormalizeGridValue {
  valueToProcess: unknown;
  fieldNameResolved: string;
  entity: RydeUiUnit;
}

interface ITranslateGridValue {
  valueToProcess: unknown;
  formAttributes: IFormAttributes | null | undefined;
  gridAttributes: IGridAttributes | null | undefined;
  /* skip converting boolean value to select list label Yes/No if selectList attribute exists */
  skipBooleanSelectListLabelConversion: boolean;
  t: TFunction<"translation", undefined>;
}

export const normalizeGridValue = ({
  valueToProcess: valueRaw,
  fieldNameResolved,
  entity,
}: INormalizeGridValue): unknown => {
  if (valueRaw === undefined || valueRaw === null || valueRaw === "") {
    return "";
  }

  if (isDurationDistanceEntity(entity)) {
    if (
      fieldNameResolved === TOTAL_DURATION_FIELD ||
      fieldNameResolved === appendMetadataPrefix(TOTAL_DURATION_FIELD)
    ) {
      if (!Number(valueRaw)) {
        return "";
      }

      return durationInMinutesCeil(Number(valueRaw)) || "";
    }

    if (
      fieldNameResolved === TOTAL_DISTANCE_FIELD ||
      fieldNameResolved === appendMetadataPrefix(TOTAL_DISTANCE_FIELD)
    ) {
      if (!Number(valueRaw)) {
        return "";
      }

      return distanceFormula(Number(valueRaw)) || "";
    }
  }

  return valueRaw;
};

export const translateGridValue = ({
  valueToProcess: valueRaw,
  formAttributes,
  gridAttributes,
  skipBooleanSelectListLabelConversion,
  t,
}: ITranslateGridValue): unknown => {
  const { translationPath } = gridAttributes || {};

  if (valueRaw === undefined || valueRaw === null || valueRaw === "") {
    return "";
  }

  if (
    typeof valueRaw === "object" &&
    valueRaw !== null &&
    hasKeys(valueRaw as object) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (valueRaw as any).value === "number"
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { value, isCurrency } = valueRaw as any;

    try {
      const isCurr = Boolean(isCurrency);
      if (isCurr) {
        return formatPrice({ value, isCurrency: true });
      }
      return `${value}%`;
    } catch {
      return value;
    }
  }

  if (formAttributes?.selectItems?.length) {
    if (skipBooleanSelectListLabelConversion && typeof valueRaw === "boolean") {
      return valueRaw;
    }

    const labelByValue = formAttributes.selectItems.find(
      i => String(i.value ?? "") === String(valueRaw),
    )?.label;

    if (labelByValue === undefined) {
      return valueRaw;
    }

    // Support both translated keys and literal Hebrew labels

    if (!translationPath) {
      return labelByValue;
    }

    const translationKey = String(labelByValue).startsWith(
      `${translationPath}.`,
    )
      ? String(labelByValue)
      : `${translationPath}.${labelByValue}`;

    return t(translationKey);
  }

  return valueRaw;
};

export const resolveSelected = ({
  selectedTableRowsIds,
  setSelectedTableRowsIds,
  tableIds,
}: {
  selectedTableRowsIds: Set<number>;
  setSelectedTableRowsIds: (
    valOrUpdater: ((currVal: number[]) => number[]) | number[],
  ) => void;
  tableIds: Set<number>;
}): void => {
  setSelectedTableRowsIds(currVal => {
    const resolved = tableIds.intersection(selectedTableRowsIds);
    if (
      resolved.size === currVal.length &&
      currVal.every(id => resolved.has(id))
    ) {
      return currVal;
    }
    return Array.from(resolved);
  });
};
